import { useState, useMemo, useEffect } from 'react'
import { Calculator, TrendingUp, Briefcase, Scale, Save, Trash2, ChevronDown, ChevronUp, RefreshCcw, Check } from 'lucide-react'
import { DashboardLayout } from '@/components/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { usePlan } from '@/hooks/usePlan'
import { useProfile, useUpdateProfile } from '@/hooks/useProfile'
import { useToast } from '@/hooks/use-toast'
import { 
  useLatestSimulation, 
  useCreateSimulation, 
  usePricingSimulations,
  useDeleteSimulation 
} from '@/hooks/usePricingSimulations'
import {
  calculateIdealHourlyRate,
  simulateProject,
  compareCLTvsPJ,
  TAX_REGIME_PRESETS,
  formatCurrency,
  formatPercentage,
  TaxRegime,
  CalculatorInputs,
  CalculatorResult,
  ProjectSimulationResult,
  CLTvsPJResult
} from '@/lib/calculator-calculations'
// TrialExpiredBlocker removido - bloqueio agora é feito no ProtectedRoute

export default function CalculatorPage() {
  const { toast } = useToast()
  const { isLoading: planLoading } = usePlan()
  const { data: profile } = useProfile()
  const updateProfile = useUpdateProfile()
  const { data: latestSimulation, isLoading: loadingLatest } = useLatestSimulation()
  const { data: allSimulations } = usePricingSimulations()
  const createSimulation = useCreateSimulation()
  const deleteSimulation = useDeleteSimulation()

  // Calculator inputs
  const [monthlyGoal, setMonthlyGoal] = useState(10000)
  const [weeklyWorkHours, setWeeklyWorkHours] = useState(40)
  const [weeksPerMonth, setWeeksPerMonth] = useState(4)
  const [taxPercentage, setTaxPercentage] = useState(15.5)
  const [vacationPercentage, setVacationPercentage] = useState(8.33)
  const [thirteenthPercentage, setThirteenthPercentage] = useState(8.33)
  const [fixedMonthlyCosts, setFixedMonthlyCosts] = useState(500)
  const [taxRegime, setTaxRegime] = useState<TaxRegime>('simples')

  // Project simulator inputs
  const [projectType, setProjectType] = useState<'hourly' | 'fixed'>('hourly')
  const [projectHours, setProjectHours] = useState(40)
  const [projectFixedPrice, setProjectFixedPrice] = useState(5000)

  // CLT vs PJ inputs
  const [cltSalary, setCltSalary] = useState(8000)
  const [cltBenefits, setCltBenefits] = useState(1500)
  const [pjTaxRate, setPjTaxRate] = useState(15.5)
  const [pjFixedCosts, setPjFixedCosts] = useState(500)

  // UI state
  const [showDeductions, setShowDeductions] = useState(false)
  const [showCLTComparison, setShowCLTComparison] = useState(false)
  const [simulationName, setSimulationName] = useState('')
  const [syncedToProfile, setSyncedToProfile] = useState(false)

  // Load latest simulation data
  useEffect(() => {
    if (latestSimulation) {
      setMonthlyGoal(Number(latestSimulation.monthly_goal))
      setWeeklyWorkHours(Number(latestSimulation.weekly_work_hours))
      setWeeksPerMonth(Number(latestSimulation.weeks_per_month))
      setTaxPercentage(Number(latestSimulation.tax_percentage))
      setVacationPercentage(Number(latestSimulation.vacation_percentage))
      setThirteenthPercentage(Number(latestSimulation.thirteenth_percentage))
      setFixedMonthlyCosts(Number(latestSimulation.fixed_monthly_costs))
      setTaxRegime(latestSimulation.tax_regime as TaxRegime)
    }
  }, [latestSimulation])

  // Apply tax regime presets
  const handleRegimeChange = (regime: TaxRegime) => {
    setTaxRegime(regime)
    const preset = TAX_REGIME_PRESETS[regime]
    setTaxPercentage(preset.taxPercentage)
    setVacationPercentage(preset.vacationPercentage)
    setThirteenthPercentage(preset.thirteenthPercentage)
  }

  // Calculate results
  const calculatorInputs: CalculatorInputs = {
    monthlyGoal,
    weeklyWorkHours,
    weeksPerMonth,
    taxPercentage,
    vacationPercentage,
    thirteenthPercentage,
    fixedMonthlyCosts,
    taxRegime
  }

  const result: CalculatorResult = useMemo(
    () => calculateIdealHourlyRate(calculatorInputs),
    [monthlyGoal, weeklyWorkHours, weeksPerMonth, taxPercentage, vacationPercentage, thirteenthPercentage, fixedMonthlyCosts]
  )

  const projectResult: ProjectSimulationResult = useMemo(
    () => simulateProject({
      type: projectType,
      estimatedHours: projectHours,
      fixedPrice: projectFixedPrice,
      idealHourlyRate: result.idealHourlyRate
    }),
    [projectType, projectHours, projectFixedPrice, result.idealHourlyRate]
  )

  const cltPjResult: CLTvsPJResult = useMemo(
    () => compareCLTvsPJ({
      desiredCLTSalary: cltSalary,
      estimatedBenefits: cltBenefits,
      pjTaxPercentage: pjTaxRate,
      pjFixedCosts: pjFixedCosts
    }),
    [cltSalary, cltBenefits, pjTaxRate, pjFixedCosts]
  )

  // Save simulation
  const handleSaveSimulation = () => {

    createSimulation.mutate({
      name: simulationName || `Simulação ${new Date().toLocaleDateString('pt-BR')}`,
      monthly_goal: monthlyGoal,
      weekly_work_hours: weeklyWorkHours,
      weeks_per_month: weeksPerMonth,
      tax_percentage: taxPercentage,
      vacation_percentage: vacationPercentage,
      thirteenth_percentage: thirteenthPercentage,
      fixed_monthly_costs: fixedMonthlyCosts,
      tax_regime: taxRegime,
      calculated_hourly_rate: result.idealHourlyRate
    })
    setSimulationName('')
  }

  // Sync calculated values to profile (optional action)
  const handleSyncToProfile = async () => {

    try {
      await updateProfile.mutateAsync({
        hourly_rate: Math.round(result.idealHourlyRate * 100) / 100,
        monthly_goal: Math.round(result.grossRevenueNeeded * 100) / 100,
      })
      setSyncedToProfile(true)
      toast({
        title: "Perfil atualizado!",
        description: `Valor/hora: ${formatCurrency(result.idealHourlyRate)} • Meta mensal: ${formatCurrency(result.grossRevenueNeeded)}`,
      })
      // Reset synced state after 3 seconds
      setTimeout(() => setSyncedToProfile(false), 3000)
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar o perfil.",
      })
    }
  }

  // Check if current values match profile
  const isProfileSynced = useMemo(() => {
    if (!profile) return false
    const hourlyMatch = Math.abs((profile.hourly_rate || 0) - result.idealHourlyRate) < 0.01
    const goalMatch = Math.abs((profile.monthly_goal || 0) - result.grossRevenueNeeded) < 0.01
    return hourlyMatch && goalMatch
  }, [profile, result.idealHourlyRate, result.grossRevenueNeeded])

  if (planLoading || loadingLatest) {
    return (
      <DashboardLayout title="Calculadora Profissional">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout title="Calculadora Profissional" subtitle="Simulador financeiro para freelancers">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <Calculator className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Calculadora Profissional</h1>
          </div>
          <p className="text-muted-foreground">
            Descubra quanto você precisa cobrar para atingir sua meta mensal real
          </p>
        </div>

        {/* Acesso garantido pelo ProtectedRoute */}

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Block 1: Meta e Horas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Meta e Disponibilidade
              </CardTitle>
              <CardDescription>
                Defina sua meta financeira e horas disponíveis
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="monthlyGoal">Meta Líquida Mensal (R$)</Label>
                <Input
                  id="monthlyGoal"
                  type="number"
                  value={monthlyGoal}
                  onChange={(e) => setMonthlyGoal(Number(e.target.value))}
                  placeholder="10000"
                />
                <p className="text-xs text-muted-foreground">
                  Quanto você quer ter disponível após impostos e reservas
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="weeklyHours">Horas/Semana</Label>
                  <Input
                    id="weeklyHours"
                    type="number"
                    value={weeklyWorkHours}
                    onChange={(e) => setWeeklyWorkHours(Number(e.target.value))}
                    placeholder="40"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weeksMonth">Semanas/Mês</Label>
                  <Input
                    id="weeksMonth"
                    type="number"
                    value={weeksPerMonth}
                    onChange={(e) => setWeeksPerMonth(Number(e.target.value))}
                    placeholder="4"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fixedCosts">Custos Fixos Mensais (R$)</Label>
                <Input
                  id="fixedCosts"
                  type="number"
                  value={fixedMonthlyCosts}
                  onChange={(e) => setFixedMonthlyCosts(Number(e.target.value))}
                  placeholder="500"
                />
                <p className="text-xs text-muted-foreground">
                  Contador, ferramentas, coworking, etc.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Block 2: Impostos e Encargos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5" />
                Impostos e Encargos
              </CardTitle>
              <CardDescription>
                Configure seu regime tributário
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="taxRegime">Regime Tributário</Label>
                <Select value={taxRegime} onValueChange={(v) => handleRegimeChange(v as TaxRegime)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o regime" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mei">MEI</SelectItem>
                    <SelectItem value="simples">Simples Nacional</SelectItem>
                    <SelectItem value="pj">PJ Lucro Presumido</SelectItem>
                    <SelectItem value="clt_simulated">CLT (Simulado)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {TAX_REGIME_PRESETS[taxRegime].description}
                </p>
              </div>

              <Collapsible open={showDeductions} onOpenChange={setShowDeductions}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between p-0 h-auto">
                    <span className="text-sm font-medium">Ajustar percentuais manualmente</span>
                    {showDeductions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="taxPct">Impostos (%)</Label>
                    <Input
                      id="taxPct"
                      type="number"
                      step="0.1"
                      value={taxPercentage}
                      onChange={(e) => setTaxPercentage(Number(e.target.value))}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="vacationPct">Férias (%)</Label>
                      <Input
                        id="vacationPct"
                        type="number"
                        step="0.1"
                        value={vacationPercentage}
                        onChange={(e) => setVacationPercentage(Number(e.target.value))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="thirteenthPct">13º (%)</Label>
                      <Input
                        id="thirteenthPct"
                        type="number"
                        step="0.1"
                        value={thirteenthPercentage}
                        onChange={(e) => setThirteenthPercentage(Number(e.target.value))}
                      />
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <Separator />

              <div className="space-y-2 p-4 bg-muted/50 rounded-xl">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total de deduções:</span>
                  <span className="font-medium">{formatPercentage(result.deductionPercentage)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Impostos:</span>
                  <span>{formatCurrency(result.taxAmount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Reserva férias:</span>
                  <span>{formatCurrency(result.vacationReserve)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Reserva 13º:</span>
                  <span>{formatCurrency(result.thirteenthReserve)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Block 3: Resultado Principal */}
        <Card className="border-primary/50 bg-gradient-to-br from-primary/5 to-transparent">
          <CardHeader>
            <CardTitle className="text-xl">Seu Valor Hora Ideal</CardTitle>
            <CardDescription>
              Baseado na sua meta de {formatCurrency(monthlyGoal)} líquidos por mês
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="text-center p-6 bg-background rounded-xl border">
                <p className="text-sm text-muted-foreground mb-2">Valor Hora Ideal</p>
                <p className="text-4xl font-bold text-primary">
                  {formatCurrency(result.idealHourlyRate)}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {result.availableHoursPerMonth}h disponíveis/mês
                </p>
              </div>

              <div className="text-center p-6 bg-background rounded-xl border">
                <p className="text-sm text-muted-foreground mb-2">Receita Bruta Necessária</p>
                <p className="text-3xl font-bold">
                  {formatCurrency(result.grossRevenueNeeded)}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Total que precisa faturar
                </p>
              </div>

              <div className="text-center p-6 bg-background rounded-xl border">
                <p className="text-sm text-muted-foreground mb-2">Receita Líquida</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-500">
                  {formatCurrency(result.netMonthlyIncome)}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Após impostos e reservas
                </p>
              </div>
            </div>

            {/* Actions */}
            {(
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-6 pt-6 border-t">
                <div className="flex items-center gap-3 flex-1">
                  <Input
                    placeholder="Nome da simulação (opcional)"
                    value={simulationName}
                    onChange={(e) => setSimulationName(e.target.value)}
                    className="max-w-xs"
                  />
                  <Button onClick={handleSaveSimulation} disabled={createSimulation.isPending}>
                    <Save className="h-4 w-4 mr-2" />
                    Salvar Simulação
                  </Button>
                </div>
                
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant={syncedToProfile || isProfileSynced ? "outline" : "secondary"}
                        onClick={handleSyncToProfile} 
                        disabled={updateProfile.isPending || isProfileSynced}
                        className="gap-2"
                      >
                        {syncedToProfile || isProfileSynced ? (
                          <>
                            <Check className="h-4 w-4 text-green-600" />
                            Sincronizado
                          </>
                        ) : (
                          <>
                            <RefreshCcw className="h-4 w-4" />
                            Aplicar ao Perfil
                          </>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Atualiza seu valor/hora e meta mensal nas configurações do perfil</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Block 4: Simulador de Projeto */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Simulador de Projeto
            </CardTitle>
            <CardDescription>
              Calcule o valor ideal para seu próximo projeto
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Tipo de Cobrança</Label>
                  <Select value={projectType} onValueChange={(v) => setProjectType(v as 'hourly' | 'fixed')}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">Por Hora</SelectItem>
                      <SelectItem value="fixed">Preço Fixo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="projectHours">Horas Estimadas</Label>
                  <Input
                    id="projectHours"
                    type="number"
                    value={projectHours}
                    onChange={(e) => setProjectHours(Number(e.target.value))}
                    placeholder="40"
                  />
                </div>

                {projectType === 'fixed' && (
                  <div className="space-y-2">
                    <Label htmlFor="fixedPrice">Valor Fechado (R$)</Label>
                    <Input
                      id="fixedPrice"
                      type="number"
                      value={projectFixedPrice}
                      onChange={(e) => setProjectFixedPrice(Number(e.target.value))}
                      placeholder="5000"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-4 p-4 bg-muted/50 rounded-xl">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Valor Total</span>
                  <span className="text-2xl font-bold">{formatCurrency(projectResult.totalValue)}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Valor/Hora Efetivo</span>
                  <span className="font-medium">{formatCurrency(projectResult.effectiveHourlyRate)}</span>
                </div>

                <Separator />

                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Mínimo Recomendado</span>
                  <span className="font-medium">{formatCurrency(projectResult.minimumRecommended)}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Margem</span>
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${projectResult.isAboveMinimum ? 'text-green-600 dark:text-green-500' : 'text-destructive'}`}>
                      {formatCurrency(projectResult.estimatedMargin)}
                    </span>
                    <Badge variant={projectResult.isAboveMinimum ? 'default' : 'destructive'}>
                      {formatPercentage(projectResult.marginPercentage)}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Block 5: CLT vs PJ (Collapsible) */}
        <Collapsible open={showCLTComparison} onOpenChange={setShowCLTComparison}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Scale className="h-5 w-5" />
                      Comparador CLT vs PJ
                    </CardTitle>
                    <CardDescription>
                      Descubra quanto precisa faturar como PJ para ter a mesma renda líquida
                    </CardDescription>
                  </div>
                  {showCLTComparison ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                    <h4 className="font-medium">Cenário CLT</h4>
                    <div className="space-y-2">
                      <Label htmlFor="cltSalary">Salário CLT Desejado (R$)</Label>
                      <Input
                        id="cltSalary"
                        type="number"
                        value={cltSalary}
                        onChange={(e) => setCltSalary(Number(e.target.value))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cltBenefits">Benefícios Estimados (R$)</Label>
                      <Input
                        id="cltBenefits"
                        type="number"
                        value={cltBenefits}
                        onChange={(e) => setCltBenefits(Number(e.target.value))}
                      />
                      <p className="text-xs text-muted-foreground">
                        VR, VT, plano de saúde, etc.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium">Cenário PJ</h4>
                    <div className="space-y-2">
                      <Label htmlFor="pjTax">Impostos PJ (%)</Label>
                      <Input
                        id="pjTax"
                        type="number"
                        step="0.1"
                        value={pjTaxRate}
                        onChange={(e) => setPjTaxRate(Number(e.target.value))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pjCosts">Custos Fixos PJ (R$)</Label>
                      <Input
                        id="pjCosts"
                        type="number"
                        value={pjFixedCosts}
                        onChange={(e) => setPjFixedCosts(Number(e.target.value))}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3 mt-6 pt-6 border-t">
                  <div className="text-center p-4 bg-muted/50 rounded-xl">
                    <p className="text-sm text-muted-foreground mb-1">Líquido CLT</p>
                    <p className="text-2xl font-bold">{formatCurrency(cltPjResult.cltNetIncome)}</p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-xl">
                    <p className="text-sm text-muted-foreground mb-1">Faturamento PJ Necessário</p>
                    <p className="text-2xl font-bold text-primary">{formatCurrency(cltPjResult.pjGrossNeeded)}</p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-xl">
                    <p className="text-sm text-muted-foreground mb-1">Diferença</p>
                    <p className={`text-2xl font-bold ${cltPjResult.differencePercentage > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      {cltPjResult.differencePercentage > 0 ? '+' : ''}{formatPercentage(cltPjResult.differencePercentage)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Saved Simulations */}
        {allSimulations && allSimulations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Simulações Salvas</CardTitle>
              <CardDescription>
                Suas simulações anteriores
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {allSimulations.slice(0, 5).map((sim) => (
                  <div 
                    key={sim.id} 
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{sim.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Meta: {formatCurrency(Number(sim.monthly_goal))} • 
                        Valor/Hora: {formatCurrency(Number(sim.calculated_hourly_rate || 0))}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteSimulation.mutate(sim.id)}
                      disabled={deleteSimulation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  )
}
